import { apiClient } from './api.js';

class DatabaseService {
    constructor() {
        this.apiClient = apiClient;
        // Map table names to controller endpoints
        this.tableMapping = {
            'manu_leaf': 'manuleaf',
            'model_leaf': 'modelleaf',
            'manu_turbine': 'manuturbine',
            'model_turbine': 'modelturbine',
            'def_defection': 'defdefection',
            'field_item': 'fielditem',
            'raw_data': 'rawdata',
            'frame_data': 'framedata'
        };
    }

    // Helper method to get the correct endpoint for a table
    getEndpoint(table) {
        return this.tableMapping[table] || table;
    }

    // Generic query method to replace db.query()
    async query(endpoint, params = {}) {
        try {
            const queryString = new URLSearchParams(params).toString();
            const url = queryString ? `${endpoint}?${queryString}` : endpoint;
            return await this.apiClient.get(url);
        } catch (error) {
            console.error('Database query failed:', error);
            throw error;
        }
    }

    // Generic select method to replace db.select()
    async select(table, params = {}) {
        try {
            const endpoint = this.getEndpoint(table);
            return await this.query(`/${endpoint}`, params);
        } catch (error) {
            console.error(`Select from ${table} failed:`, error);
            throw error;
        }
    }

    // Generic create method to replace db.create()
    async create(table, data) {
        try {
            const endpoint = this.getEndpoint(table);
            return await this.apiClient.post(`/${endpoint}`, data);
        } catch (error) {
            console.error(`Create in ${table} failed:`, error);
            throw error;
        }
    }

    // Generic update method to replace db.update()
    async update(table, id, data) {
        try {
            const endpoint = this.getEndpoint(table);
            return await this.apiClient.put(`/${endpoint}/${id}`, data);
        } catch (error) {
            console.error(`Update ${table}/${id} failed:`, error);
            throw error;
        }
    }

    // Generic delete method to replace db.delete()
    async delete(recordIdOrTable, id) {
        try {
            console.log('Delete called with:', recordIdOrTable, id);
            console.log('Arguments length:', arguments.length);
            
            let table, actualId;
            
            // Handle SurrealDB record format (single parameter)
            if (arguments.length === 1) {
                const recordId = recordIdOrTable;
                console.log('Single parameter recordId:', recordId, typeof recordId);
                if (typeof recordId === 'string') {
                    if (recordId.includes(':')) {
                        [table, actualId] = recordId.split(':');
                        console.log('Extracted table:', table, 'id:', actualId);
                    } else {
                        throw new Error('Invalid record ID format');
                    }
                } else if (recordId && recordId.tb && recordId.id) {
                    table = recordId.tb;
                    actualId = recordId.id;
                    console.log('Object format - table:', table, 'id:', actualId);
                } else {
                    throw new Error('Invalid record ID format');
                }
            } else {
                // Handle two-parameter format (table, id)
                table = recordIdOrTable;
                actualId = id;
                console.log('Two parameter format - table:', table, 'id:', actualId);
            }
            
            const endpoint = this.getEndpoint(table);
            console.log('Final endpoint:', `/${endpoint}/${actualId}`);
            return await this.apiClient.delete(`/${endpoint}/${actualId}`);
        } catch (error) {
            console.error(`Delete ${recordIdOrTable}${id ? '/' + id : ''} failed:`, error);
            throw error;
        }
    }

    // Generic merge method to replace db.merge()
    async merge(recordId, data) {
        try {
            // Extract table and id from SurrealDB record format
            let table, id;
            if (typeof recordId === 'string') {
                if (recordId.includes(':')) {
                    [table, id] = recordId.split(':');
                } else {
                    throw new Error('Invalid record ID format');
                }
            } else if (recordId && recordId.tb && recordId.id) {
                table = recordId.tb;
                id = recordId.id;
            } else {
                throw new Error('Invalid record ID format');
            }
            
            const endpoint = this.getEndpoint(table);
            return await this.apiClient.put(`/${endpoint}/${id}`, data);
        } catch (error) {
            console.error(`Merge ${recordId} failed:`, error);
            throw error;
        }
    }

    // Specific methods for common operations
    async getFieldItems() {
        return this.apiClient.getFieldItems();
    }

    async getUsers() {
        return this.apiClient.getUsers();
    }

    async getTasks() {
        return this.apiClient.getTasks();
    }

    async getTurbines() {
        return this.apiClient.getTurbines();
    }

    async getLeaves() {
        return this.apiClient.getLeaves();
    }

    async getDefDefections() {
        return this.apiClient.getDefDefections();
    }

    async getRawData(taskId) {
        return this.apiClient.getRawData(taskId);
    }

    async getFrameData(rawDataId) {
        return this.apiClient.getFrameData(rawDataId);
    }

    async getDefections(frameDataId) {
        return this.apiClient.getDefections(frameDataId);
    }

    // Utility methods for compatibility
    async use(namespace, database) {
        // This is now handled by the backend API
        console.log(`Using namespace: ${namespace}, database: ${database}`);
        return Promise.resolve();
    }

    async signin(credentials) {
        // Use the API client login method
        return await this.apiClient.login(credentials.account, credentials.password);
    }
}

// Export singleton instance to replace direct SurrealDB usage
export const db = new DatabaseService();
export default db;